import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ObjectProposalGenerationType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import ESpinner from 'components/others/Spinner';
import TabAddPayment from './tabs/TabAddPayment';
import TabPaymentsList from './tabs/TabPaymentsList';
import TabDocument from './tabs/TabDocument';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import SolarIcon from 'components/icons/SolarIcon';

export type DocumentOffcanvasTab = 'document' | 'payments' | 'add-payment';

export interface DocumentPaymentTarget {
    generationId: number;
    title: string;
    initialTab: 'document' | 'payments';
    row: ObjectProposalGenerationType;
}

interface Props {
    objectId: number;
    showTarget: DocumentPaymentTarget | null;
    setShowTarget: Dispatch<SetStateAction<DocumentPaymentTarget | null>>;
    onPaid?: () => Promise<void> | void;
    onSendEmail?: (row: ObjectProposalGenerationType) => void;
    readOnly?: boolean;
}

const canPayDocument = (
    row: ObjectProposalGenerationType | undefined,
    objectPaymentControl: boolean,
) =>
    Boolean(objectPaymentControl)
    && (row?.source || 'object') === 'object'
    && !Number(row?.deleted || 0);

const OffDocumentPayments = ({
    objectId,
    showTarget,
    setShowTarget,
    onPaid,
    onSendEmail,
    readOnly = false,
}: Props) => {
    const { t } = useTranslation(['Objects']);
    const { showToast } = useToast();
    const params = useSettingsParams();
    const [activeTab, setActiveTab] = useState<DocumentOffcanvasTab>('document');
    const [payments, setPayments] = useState<Record<string, any>[] | null>(null);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(false);
    const [recommendAmount, setRecommendAmount] = useState(0);
    const [isUsd, setIsUsd] = useState(false);
    const [entered, setEntered] = useState(false);
    const canPay = !readOnly && canPayDocument(showTarget?.row, Boolean(params.objectPaymentControl));
    const openKey = showTarget
        ? `${showTarget.generationId}:${showTarget.initialTab}:${params.objectPaymentControl ? 1 : 0}:${readOnly ? 1 : 0}`
        : '';
    const [syncedOpenKey, setSyncedOpenKey] = useState('');

    if (openKey !== syncedOpenKey) {
        setSyncedOpenKey(openKey);
        if (!showTarget) {
            setActiveTab('document');
        } else {
            const allowPayments = !readOnly && canPayDocument(showTarget.row, Boolean(params.objectPaymentControl));
            setActiveTab(showTarget.initialTab === 'payments' && allowPayments ? 'payments' : 'document');
        }
    }

    const fetchPayments = async () => {
        if (!showTarget?.generationId || !objectId) return;
        setFetching(true);
        setError(false);
        const res = await apiDocument.getObjectDocumentPayments(objectId, showTarget.generationId);
        if (!res) {
            setError(true);
            setFetching(false);
            showToast(t('common:messError'), 'danger');
            return;
        }
        setPayments(res.payments || []);
        const balance = safeFloatParse(res.generation?.balance ?? 0);
        const amount = safeFloatParse(res.generation?.proposalAmount ?? 0);
        const paid = safeFloatParse(res.generation?.proposalPay ?? 0);
        setRecommendAmount(balance > 0 ? balance : Math.max(0, amount - paid));
        setIsUsd(Boolean(Number(res.generation?.proposalUsd ?? 0)));
        setFetching(false);
    };

    const handleHide = () => {
        setShowTarget(null);
        setActiveTab('document');
        setPayments(null);
        setEntered(false);
    };

    useEffect(() => {
        if (!showTarget?.generationId || !canPay) {
            setPayments(null);
            setFetching(false);
            return;
        }
        void fetchPayments();
    }, [showTarget?.generationId, canPay]);

    return (
        <Offcanvas
            className="off-import"
            show={showTarget !== null}
            onHide={handleHide}
            onEntered={() => setEntered(true)}
            onExited={() => setEntered(false)}
            placement="end"
        >
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{showTarget?.title || t('documents.payments.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Tabs
                    variant="underline"
                    id="off-document-payments-tab"
                    className="b-3 invoice-tab border-bottom"
                    activeKey={activeTab}
                    onSelect={(k) => k && setActiveTab(k as DocumentOffcanvasTab)}
                >
                    <Tab
                        eventKey="document"
                        title={
                            <>
                                <SolarIcon name="file-earmark-text" className="me-2 fs-14" />
                                {t('documents.tabDocument')}
                            </>
                        }
                    >
                        {showTarget ? (
                            <TabDocument
                                key={`${showTarget.row.source || 'object'}-${showTarget.generationId}`}
                                row={showTarget.row}
                                active={activeTab === 'document' && entered}
                                onSendEmail={onSendEmail}
                                readOnly={readOnly}
                            />
                        ) : null}
                    </Tab>
                    {canPay ? (
                        <Tab
                            eventKey="add-payment"
                            title={
                                <>
                                    <SolarIcon name="plus-circle" className="me-2 fs-14" />
                                    {t('documents.payments.tabAdd')}
                                </>
                            }
                        >
                            {fetching ? (
                                <ESpinner />
                            ) : showTarget ? (
                                <TabAddPayment
                                    objectId={objectId}
                                    generationId={showTarget.generationId}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab as Dispatch<SetStateAction<string>>}
                                    recommendAmount={recommendAmount}
                                    isUsd={isUsd}
                                    fetchPayments={fetchPayments}
                                    onPaid={onPaid}
                                    onClose={handleHide}
                                />
                            ) : null}
                        </Tab>
                    ) : null}
                    {canPay ? (
                        <Tab
                            eventKey="payments"
                            title={
                                <>
                                    <SolarIcon name="cash-coin" className="me-2 fs-14" />
                                    {t('documents.payments.tabList')}
                                </>
                            }
                        >
                            {fetching ? <ESpinner /> : <TabPaymentsList payments={payments} error={error} />}
                        </Tab>
                    ) : null}
                </Tabs>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffDocumentPayments;
